import React from 'react';
import EasternMapDialog from 'src/components/project/sections/TractComponents/dialogs/EasternMapDialog';
import EasternTownshipDialog from 'src/components/project/sections/TractComponents/dialogs/EasternTownshipDialog';
import TownshipDialog from 'src/components/project/sections/tract/dialogs/TownshipDialog';
import SectionDialog from 'src/components/project/sections/tract/dialogs/SectionDialog';
import TagsDialog from 'src/components/project/sections/tract/dialogs/TagsDialog';
import AltSubCodeSection from 'src/components/project/sections/AltSubCodeSection';
import { LandSystem } from 'src/models/LandSystem';
import ProjectStaff from 'src/models/ProjectStaff';
import StaffingLandmanDialogRedesign from 'src/components/project/sections/StaffingLandmanDialog.redesign';
import BillingAndPaymentCycle from 'src/models/BillingAndPaymentCycle';
import User from 'src/models/User';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import Landman from 'src/models/Landman';
import RemoveLandmanDialog from 'src/components/project/sections/RemoveLandmanDialog';
import ProjectWorkClassification from 'src/models/ProjectWorkClassification';
import Acknowledgement from 'src/models/leasing/Acknowledgement';
import State from 'src/models/State';
import AcknowledgementType from 'src/models/leasing/AcknowledgementType';
import AcknowledgementDialog from 'src/components/project/sections/LeasingComponents/redesign/AcknowledgementDialog';
import DeleteAcknowledgementDialog from 'src/components/project/sections/LeasingComponents/redesign/DeleteAcknowledgementDialog';
import AcknowledgementDialogRTE from 'src/components/project/sections/LeasingComponents/redesign/AcknowledgementDialog.redesign';
import County from 'src/models/County';
import ROWLine from 'src/models/ROWLine';
import AddUpdateROWLineDialog from 'src/components/project/sections/tract/dialogs/AddUpdateROWLineDialog';

export type dialogCreator = {
    addUpdateEasternMap: (
        townshipId: number,
        mapNumber: number,
        action: 'create' | 'edit' | 'delete',
        onUpdateComplete: (easternMapNumber: number) => void
    ) => void;
    addUpdateDeleteEasternTownship: (
        townshipId: number,
        action: 'create' | 'edit' | 'delete',
        onUpdateComplete?: (townshipId: number) => void
    ) => void;
    addUpdateDeleteTownship: (
        townshipId: number,
        action: 'create' | 'edit' | 'delete',
        landSystem: LandSystem,
        onUpdateComplete: (townshipId: number) => void
    ) => void;
    addUpdateDeleteSection: (
        townshipId: number,
        sectionNumber: number,
        action: 'create' | 'edit' | 'delete',
        landSystem: LandSystem,
        onUpdateComplete: () => void
    ) => void;
    addUpdateProjectTags: (
        action: 'create' | 'edit' | 'delete',
        onUpdateComplete: (nextPage: boolean) => void,
        tractNumber?: number,
        tractId?: number,
        sectionNumber?: number,
        surfaceTractId?: number
    ) => void;
    addAltSubCode: (subCodeId: number) => void;
    addEditLandmanInfo: (
        landmanInfo: ProjectStaff,
        billingCycle: BillingAndPaymentCycle[],
        pcapmList: User[],
        projectAdminStaff: ProjectAdministratorStaff[],
        workClassifications: ProjectWorkClassification[],
        updateCardListener: (data: boolean) => void
    ) => void;
    addRemoveLandman: (
        projectStaff: ProjectStaff,
        landman: Landman,
        workClassifications: ProjectWorkClassification[],
        deStaffedListener: (data: boolean) => void
    ) => void;
    addEditAcknowledgement: (
        acknowledgement: Acknowledgement,
        states: State[],
        acknowledgementTypes: AcknowledgementType[],
        refreshLease: (data: boolean) => void,
        newAcknowledgement: boolean,
        olderVersions: boolean,
        invoiceableLookupId: number,
        hideDialog: () => void
    ) => void;
    addDeleteAcknowledgement: (
        acknowledgement: Acknowledgement,
        onCloseDialog: () => void,
        refreshLease: (data: boolean) => void,
    ) => void;
    addEditAcknowledgementRTE: (
        acknowledgement: Acknowledgement,
        acknowledgementTypes?: AcknowledgementType[],
        newAcknowledgement?: boolean,
        refreshLease?: (refresh: boolean) => void,
        invoiceableLookupId?: number
    ) => void;
    addEditROWLine: (
        rowLine: ROWLine,
        counties: Array<County>,
        onUpdate: () => void,
    ) => void;
};

const creators = (setDialog: (element: React.ReactElement) => void): dialogCreator => {
    return {
        addUpdateDeleteTownship: (
            townshipId: number,
            action: 'create' | 'edit' | 'delete',
            landSystem: LandSystem,
            onUpdateComplete: (townshipId: number) => void
        ) => {
            setDialog(React.createElement(TownshipDialog, { townshipId, action, landSystem, onUpdateComplete }));
        },
        addUpdateEasternMap: (
            townshipId: number,
            mapNumber: number,
            action: 'create' | 'edit' | 'delete',
            onUpdateComplete: (easternMapNumber: number) => void
        ) => {
            setDialog(React.createElement(EasternMapDialog, { townshipId, mapNumber, action, onUpdateComplete }));
        },
        addUpdateDeleteEasternTownship: (
            townshipId: number,
            action: 'create' | 'edit' | 'delete',
            onUpdateComplete: (townshipId: number) => void
        ) => {
            setDialog(React.createElement(EasternTownshipDialog, { townshipId, action, onUpdateComplete }));
        },
        addUpdateDeleteSection: (
            townshipId: number,
            sectionNumber: number,
            action: 'create' | 'edit' | 'delete',
            landSystem: LandSystem,
            onUpdateComplete: () => void
        ) => {
            setDialog(React.createElement(SectionDialog, { townshipId, sectionNumber, action, landSystem, onUpdateComplete }));
        },
        addUpdateProjectTags: (
            action: 'create' | 'edit' | 'delete',
            onUpdateComplete: (nextpage: boolean) => void,
            tractNumber?: number,
            tractId?: number,
            sectionNumber?: number,
            surfaceTractId?: number
        ) => {
            setDialog(React.createElement(TagsDialog, { tractNumber, tractId, sectionNumber, surfaceTractId, action, onUpdateComplete }));
        },
        addAltSubCode: (subCodeId: number) => {
            setDialog(React.createElement(AltSubCodeSection, { subCodeId }));
        },
        addEditLandmanInfo: (
            landmanInfo: ProjectStaff,
            billingCycle: BillingAndPaymentCycle[],
            pcapmList: User[],
            projectAdminStaff: ProjectAdministratorStaff[],
            workClassifications: ProjectWorkClassification[],
            updateCardListener: (data: boolean) => void
        ) => {
            setDialog(
                React.createElement(StaffingLandmanDialogRedesign, {
                    landmanInfo,
                    billingCycle,
                    pcapmList,
                    projectAdminStaff,
                    workClassifications,
                    updateCardListener,
                })
            );
        },
        addRemoveLandman(
            projectStaff: ProjectStaff,
            landman: Landman,
            workClassifications: ProjectWorkClassification[],
            deStaffedListener: (data: boolean) => void
        ) {
            setDialog(React.createElement(RemoveLandmanDialog, { projectStaff, landman, workClassifications, deStaffedListener }));
        },
        addEditAcknowledgement(
            acknowledgement: Acknowledgement,
            states: State[],
            acknowledgementTypes: AcknowledgementType[],
            refreshLease: (data: boolean) => void,
            newAcknowledgement: boolean,
            olderVersions: boolean,
            invoiceableLookupId: number,
            hideDialog: () => void
        ) {
            setDialog(
                React.createElement(AcknowledgementDialog, {
                    acknowledgement,
                    states,
                    acknowledgementTypes,
                    refreshLease,
                    newAcknowledgement,
                    olderVersions,
                    invoiceableLookupId,
                    hideDialog,
                })
            );
        },
        addDeleteAcknowledgement(
            acknowledgement: Acknowledgement,
            onCloseDialog: () => void,
            refreshLease: (data: boolean) => void,
        ) {
            setDialog(
                React.createElement(DeleteAcknowledgementDialog, {
                    acknowledgement,
                    onCloseDialog,
                    refreshLease,
                })
            );
        },
        addEditAcknowledgementRTE(
            acknowledgement: Acknowledgement,
            acknowledgementTypes?: AcknowledgementType[],
            newAcknowledgement?: boolean,
            refreshLease?: (refresh: boolean) => void,
            invoiceableLookupId?: number
        ) {
            setDialog(
                React.createElement(AcknowledgementDialogRTE, {
                    acknowledgement,
                    acknowledgementTypes,
                    newAcknowledgement,
                    refreshLease,
                    invoiceableLookupId
                })
            );
        },
        addEditROWLine(
            rowLine: ROWLine,
            counties: Array<County>,
            onUpdate: () => void,
        ) {
            setDialog(
                React.createElement(AddUpdateROWLineDialog, {
                    rowLine,
                    counties,
                    onUpdate: onUpdate
                })
            );
        }
    };
};

export default creators;



// WEBPACK FOOTER //
// ./src/providers/dialogs/project.ts